import moment from "moment";

export const WEEKDAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const normalizeWeekdayList = (values) => {
  const allowed = new Set(WEEKDAY_KEYS);
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((raw) => {
    const key = `${raw || ""}`.trim().toLowerCase();
    if (allowed.has(key)) seen.add(key);
  });
  return WEEKDAY_KEYS.filter((day) => seen.has(day));
};

export const parseCustomerScheduleDate = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.clone().startOf("day");
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).startOf("day");
  }
  const raw = `${value}`.trim();
  const m = moment(raw, ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m.startOf("day") : null;
};

/** Convert route schedule_date (MM/DD/YYYY) or similar into moment. */
export const parseRouteScheduleDate = (scheduleDate) => {
  const raw = `${scheduleDate || ""}`.trim();
  if (!raw) return null;
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return moment(
      `${slash[3]}-${String(slash[1]).padStart(2, "0")}-${String(slash[2]).padStart(2, "0")}`,
      "YYYY-MM-DD",
      true
    ).startOf("day");
  }
  return parseCustomerScheduleDate(raw);
};

export const absenceCoversDate = (item, dayMoment) => {
  const start = parseCustomerScheduleDate(item?.start_date);
  const end = parseCustomerScheduleDate(item?.end_date);
  if (!start || !end || !dayMoment) return false;
  return (
    dayMoment.isSameOrAfter(start, "day") && dayMoment.isSameOrBefore(end, "day")
  );
};

export const isScheduleLeaveAbsence = (item) => {
  const type = `${item?.type || "leave"}`.toLowerCase();
  if (type !== "leave") return false;
  const source = `${item?.source || "schedule_leave"}`.toLowerCase();
  return source !== "day_override";
};

export const isDayOverrideAbsence = (item) =>
  `${item?.source || ""}`.toLowerCase() === "day_override";

/** Default coming days come only from customer.days_of_week. */
export const getCustomerDefaultAttendanceDays = (customer) =>
  normalizeWeekdayList(customer?.days_of_week);

/**
 * Day-status rules:
 * 1) schedule leave covering day → leave (locked)
 * 2) day_override attend → coming
 * 3) day_override leave → leave
 * 4) default weekday in customer.days_of_week → coming else leave
 */
export const getCustomerDayStatus = (
  customer,
  dayInput,
  { absencesByCustomerId = {} } = {}
) => {
  const dayMoment = parseCustomerScheduleDate(dayInput) || parseRouteScheduleDate(dayInput);
  if (!customer || !dayMoment) {
    return { status: "leave", locked: false, source: "default" };
  }

  const customerId = `${customer?.id || customer?.customer_id || ""}`;
  const absences = absencesByCustomerId[customerId] || [];

  const scheduleLeave = absences.find(
    (item) => isScheduleLeaveAbsence(item) && absenceCoversDate(item, dayMoment)
  );
  if (scheduleLeave) {
    return { status: "leave", locked: true, source: "schedule_leave", absence: scheduleLeave };
  }

  const dayAttend = absences.find(
    (item) =>
      isDayOverrideAbsence(item) &&
      `${item?.type || ""}`.toLowerCase() === "attend" &&
      absenceCoversDate(item, dayMoment)
  );
  if (dayAttend) {
    return { status: "coming", locked: false, source: "day_override", absence: dayAttend };
  }

  const dayLeave = absences.find(
    (item) =>
      isDayOverrideAbsence(item) &&
      `${item?.type || "leave"}`.toLowerCase() === "leave" &&
      absenceCoversDate(item, dayMoment)
  );
  if (dayLeave) {
    return { status: "leave", locked: false, source: "day_override", absence: dayLeave };
  }

  const weekdayKey = dayMoment.format("dddd").toLowerCase();
  const defaultComing = getCustomerDefaultAttendanceDays(customer).includes(weekdayKey);

  return {
    status: defaultComing ? "coming" : "leave",
    locked: false,
    source: "default",
  };
};

export const isCustomerOnLeaveForDate = (customer, dayInput, context) =>
  getCustomerDayStatus(customer, dayInput, context).status === "leave";

export const indexAbsencesByCustomerId = (absences = []) => {
  const map = {};
  (absences || []).forEach((item) => {
    const id = `${item?.customer_id || ""}`.trim();
    if (!id) return;
    if (!map[id]) map[id] = [];
    map[id].push(item);
  });
  return map;
};

/**
 * Build leave rows for Scheduled Absences on a route date.
 * Includes schedule leave, day_override leave, and default leave from
 * customer.days_of_week (e.g. customer not coming on Tuesdays).
 */
export const buildScheduleLeaveCustomersForDate = (
  customers = [],
  scheduleDate,
  absencesByCustomerId = {}
) => {
  const dayMoment = parseRouteScheduleDate(scheduleDate);
  if (!dayMoment) return [];

  const context = { absencesByCustomerId };
  const result = [];
  const weekdayLabel = dayMoment.format("dddd");

  (customers || []).forEach((customer) => {
    if (!customer?.id) return;
    const status = getCustomerDayStatus(customer, dayMoment, context);
    if (status.status !== "leave") return;

    const noteParts = [];
    if (status.source === "schedule_leave" && status.absence) {
      const leaveNote = `${status.absence.note || ""}`.trim();
      if (leaveNote) noteParts.push(leaveNote);
      else noteParts.push("Scheduled leave");
    } else if (status.source === "day_override") {
      noteParts.push("Temporary leave");
    } else if (status.source === "default") {
      noteParts.push(`Not scheduled (${weekdayLabel})`);
    }

    result.push({
      customer_id: customer.id,
      customer_name: customer.name_cn
        ? `${customer.name} (${customer.name_cn})`
        : customer.name,
      customer_address: customer.address1 || "",
      customer_route_status: "scheduledAbsent",
      customer_pickup_status: "scheduleAbsent",
      attendance_note: noteParts.join("; "),
      _schedule_leave_based: true,
      _leave_source: status.source,
    });
  });

  return result;
};
